// Demo files: uploads to the bill inbox, files attached to documents and
// journals, and logos. Follows the API and database: the bytes decide what a
// file is, the same file twice is one file, files on posted records and
// journals stay, a bill waiting in the inbox can't be attached elsewhere.
// There's no virus scanner in the demo, so uploads are marked clean at once.
import { demoStore } from './mockDocuments';
import { addUploadedBill, inboxHolds } from './mockInbox';
import { journalById } from './mockLedger';
import { imageSize, LOGO_TYPES, MAX_ATTACHMENT_BYTES, MAX_LOGO_BYTES, readHead, sniff } from './files';
import type { Attachment, BrandAsset, LedgerSource, UploadTarget } from './source';

type FilesPart = Pick<LedgerSource, 'attachmentFile' | 'uploadAttachment' | 'unlinkAttachment' | 'brandAssets' | 'uploadBrandAsset' | 'brandAssetFile'>;

const delay = <T,>(v: T, ms = 60): Promise<T> => new Promise((r) => setTimeout(() => r(v), ms));
const fail = (detail: string) => Promise.reject(new Error(detail));

interface StoredFile { id: string; key: string; file_name: string; content_type: string; size_bytes: number; blob: Blob | null }
interface Link { attachment_id: string; document_id?: string; journal_id?: string; linked_at: string }
interface StoredAsset { asset: Omit<BrandAsset, 'used_by'>; key: string; blob: Blob }

let files: StoredFile[] = [];
let links: Link[] = [];
let assets: StoredAsset[] = [];
let seq = 0;
const now = () => new Date().toISOString();

function seed() {
  seq = 0;
  // Seeded files have no bytes behind them; the screens show a stand-in.
  files = [
    { id: 'att1', key: 'seed:dw88412', file_name: 'DW-88412.pdf', content_type: 'application/pdf', size_bytes: 84_211, blob: null },
    { id: 'att2', key: 'seed:eir0926', file_name: 'Eir_Sept_2026.pdf', content_type: 'application/pdf', size_bytes: 152_904, blob: null },
    { id: 'att3', key: 'seed:register', file_name: 'Fixed asset register Q3.pdf', content_type: 'application/pdf', size_bytes: 61_337, blob: null },
  ];
  links = [
    { attachment_id: 'att1', document_id: 'd10', linked_at: '2026-09-03T08:40:00Z' },
    { attachment_id: 'att2', document_id: 'd11', linked_at: '2026-09-27T10:05:00Z' },
    { attachment_id: 'att3', journal_id: 'j419', linked_at: '2026-09-30T16:20:00Z' },
  ];
  assets = [];
}
seed();

export function resetDemoFiles() {
  seed();
}

function view(f: StoredFile, l: Link): Attachment {
  const d = l.document_id ? demoStore.get(l.document_id) : undefined;
  return {
    id: f.id, file_name: f.file_name, content_type: f.content_type, size_bytes: f.size_bytes, scan_status: 'clean',
    linked_at: l.linked_at, can_remove: !l.journal_id && d?.status === 'draft',
  };
}

/** What's attached to a document or journal, oldest first. */
export function attachmentsFor(q: { documentId?: string; journalId?: string }): Attachment[] {
  return links
    .filter((l) => (q.documentId && l.document_id === q.documentId) || (q.journalId && l.journal_id === q.journalId))
    .sort((a, b) => a.linked_at.localeCompare(b.linked_at))
    .map((l) => view(files.find((f) => f.id === l.attachment_id)!, l));
}

export function brandAssetExists(id: string): boolean {
  return assets.some((a) => a.asset.id === id);
}

/** For the template check: designs that use each picture. */
let templateNames: () => { name: string; logo?: string | null }[] = () => [];
export function setTemplateLookup(fn: typeof templateNames) {
  templateNames = fn;
}

const fingerprint = (head: Uint8Array, size: number) => `${size}:${Array.from(head.slice(0, 256)).join(',')}`;

export const demoFiles: FilesPart = {
  attachmentFile: (id) => delay(files.find((f) => f.id === id)?.blob ?? null),

  uploadAttachment: async (file: File, target: UploadTarget = {}) => {
    if (target.document_id && target.journal_id) return fail('Attach the file to a document or a journal, not both');
    const toInbox = target.to_inbox ?? (!target.document_id && !target.journal_id);
    if (toInbox && (target.document_id || target.journal_id)) return fail('A bill for the inbox can’t also be attached to a record');
    if (file.size === 0) return fail('The file is empty');
    if (file.size > MAX_ATTACHMENT_BYTES) return fail('Files can be up to 25 MB');
    const head = await readHead(file);
    const type = sniff(head);
    if (!type) return fail('Only PDFs and pictures (JPEG, PNG, HEIC, WebP, TIFF) can be attached');

    const key = fingerprint(head, file.size);
    let f = files.find((x) => x.key === key);
    const isNew = !f;
    if (!f) {
      f = { id: `att-u${++seq}`, key, file_name: file.name || 'file', content_type: type, size_bytes: file.size, blob: new Blob([file], { type }) };
      files.push(f);
    }

    let inbox_id: string | null = inboxHolds(f.id);
    if (toInbox && isNew) inbox_id = addUploadedBill(f.id, f.file_name, now());

    if (target.document_id || target.journal_id) {
      if (target.document_id && !demoStore.get(target.document_id)) return fail('That document no longer exists.');
      if (target.journal_id && !journalById(target.journal_id)) return fail('That journal no longer exists.');
      if (inboxHolds(f.id)) return fail('That file is waiting in the bill inbox; approve or remove it there');
      const already = links.some((l) => l.attachment_id === f!.id && l.document_id === target.document_id && l.journal_id === target.journal_id);
      if (!already) links.push({ attachment_id: f.id, document_id: target.document_id, journal_id: target.journal_id, linked_at: now() });
    }
    return delay({ attachment_id: f.id, inbox_id, is_new: isNew });
  },

  unlinkAttachment: (attachmentId, documentId) => {
    const i = links.findIndex((l) => l.attachment_id === attachmentId && l.document_id === documentId);
    if (i < 0) return fail('That file isn’t attached there');
    if (demoStore.get(documentId)?.status !== 'draft') return fail('Files attached to posted records stay attached');
    links.splice(i, 1);
    return delay(undefined);
  },

  brandAssets: () => {
    const designs = templateNames();
    return delay(assets
      .map(({ asset }) => ({ ...asset, used_by: designs.filter((t) => t.logo === asset.id).map((t) => t.name) }))
      .sort((a, b) => b.created_at.localeCompare(a.created_at)));
  },

  uploadBrandAsset: async (file, kind) => {
    if (file.size === 0) return fail('The file is empty');
    if (file.size > MAX_LOGO_BYTES) return fail('Files can be up to 2 MB');
    const head = await readHead(file);
    const type = sniff(head);
    if (!type || !(LOGO_TYPES as readonly string[]).includes(type)) {
      return fail('Logos must be PNG, JPEG or WebP (a PNG with a transparent background prints best)');
    }
    const size = imageSize(head);
    if (!size) return fail('That picture couldn’t be read; try saving it again as a PNG');
    if (size.width < 16 || size.height < 16 || size.width > 6000 || size.height > 6000) {
      return fail('The picture must be between 16 and 6,000 pixels each way');
    }
    const key = `${kind}:${fingerprint(head, file.size)}`;
    const same = assets.find((a) => a.key === key);
    if (same) return delay({ id: same.asset.id, width_px: same.asset.width_px, height_px: same.asset.height_px });
    const asset = { id: `logo-${++seq}`, kind, content_type: type, width_px: size.width, height_px: size.height, size_bytes: file.size, created_at: now() };
    assets.push({ asset, key, blob: new Blob([file], { type }) });
    return delay({ id: asset.id, width_px: asset.width_px, height_px: asset.height_px });
  },

  brandAssetFile: (id) => delay(assets.find((a) => a.asset.id === id)?.blob ?? null),
};
